import { useCallback, useEffect, type MutableRefObject, type RefObject } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { usePeekResults } from './usePeekResults';
import type { QueryExecuteSource } from './useQueryExecution';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { QueryEditorHandle } from '../components/QueryEditor';

type ExecuteOptions = { includeActualPlan?: boolean; source?: QueryExecuteSource };

type UsePeekWiringParams = {
  tabId: string;
  connectionId: string | undefined;
  database: string | undefined;
  engineId: DatabaseEngineId;
  isExecuting: boolean;
  isActive: boolean;
  editorRef: RefObject<QueryEditorHandle | null>;
  queryRef: MutableRefObject<string>;
  lastStoredQueryRef: MutableRefObject<string>;
  setQuery: (query: string) => void;
  /** Ref consumed by the selection-change handler declared before this hook runs. */
  closePeekRef: MutableRefObject<(() => void) | null>;
  trackExecutionScope: (queryText: string) => void;
  executeCurrentQuery: (queryText: string, options?: ExecuteOptions) => Promise<unknown>;
};

/**
 * Peek Results wiring for a query editor tab: the `usePeekResults` popup
 * state, the "Open in Results" promotion (replace the buffer with the peeked
 * SQL and execute it for real), the closePeekRef mirror, and the
 * close-on-execute / close-on-deactivate effects. Behavior carried verbatim
 * from the previous inline implementation.
 */
export function usePeekWiring({
  tabId,
  connectionId,
  database,
  engineId,
  isExecuting,
  isActive,
  editorRef,
  queryRef,
  lastStoredQueryRef,
  setQuery,
  closePeekRef,
  trackExecutionScope,
  executeCurrentQuery,
}: UsePeekWiringParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  // Peek Results (inline preview of selected SQL)
  const handleOpenPeekInResults = useCallback((peekQuery: string) => {
    // Set editor content to the peek query and execute it
    editorRef.current?.setValue(peekQuery);
    queryRef.current = peekQuery;
    lastStoredQueryRef.current = peekQuery;
    setQuery(peekQuery);
    updateTabContent(tabId, { query: peekQuery });
    trackExecutionScope(peekQuery);
    executeCurrentQuery(peekQuery, { source: 'peek-results' });
  }, [tabId, updateTabContent, executeCurrentQuery, trackExecutionScope, queryRef, lastStoredQueryRef, setQuery, editorRef]);

  const { isPeekExecuting, handlePeek, closePeek, peekState, peekAnchor } = usePeekResults({
    editorRef,
    connectionId,
    database,
    engineId,
    tabId,
  });
  closePeekRef.current = closePeek;

  const handleOpenPeekInResultsAndClose = useCallback((peekQuery: string) => {
    closePeek();
    handleOpenPeekInResults(peekQuery);
  }, [closePeek, handleOpenPeekInResults]);

  // Close peek panel when main query starts executing or tab becomes inactive
  useEffect(() => {
    if (isExecuting) closePeek();
  }, [isExecuting, closePeek]);

  useEffect(() => {
    if (!isActive) closePeek();
  }, [isActive, closePeek]);

  return {
    isPeekExecuting,
    handlePeek,
    closePeek,
    peekState,
    peekAnchor,
    handleOpenPeekInResultsAndClose,
  };
}
